import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import PATHS from "../../../Routes/paths";

import { ChartLegend, Switcher } from "../../../Components/Atoms";
import { ModelPicker } from "../../../Components/Molecules";
import { Stack } from "../../../Components/Organisms/Graphs";
import { Report } from "../../../Templates";

import { StoreContext } from "../../../Library/contexts";
import { getStatsData, getWidgetData } from "../../../APIs";
import { DATE_RANGES } from "../../../Library/constants";
import { MODULE_TYPES, processReportData } from "../../../Library/widgets";

const labelData = [ "0-15", "15-30", "30-45", "45-60", "60+" ];
const parser = { x: "model", y: "count", xLabel: "Vehicles Count", yLabel: "KMs / day" };
const paths = [
  { idx: 0, name: "Analytics", link: PATHS.ANALYTICS },
  { idx: 1, name: "Average Running Distance", isCurrentPage: true },
];

const processData = (data, constants) => {
  const graphs = [];
  const legends = [];

  data.forEach((d) => {
    let counts = d.count.map(({ count }) => count);

    graphs.push({
      color: constants.modelMap[d.model].color,
      model: constants.modelMap[d.model].nm,
      count: counts,
      modelId: d.model,
    });

    legends.push({ label: constants.modelMap[d.model].nm, color: constants.modelMap[d.model].color, modelId: d.model });
  });

  return { graphs, legends };
};

const AverageRunningReport = () => {
  const { filters, constants } = useContext(StoreContext);

  const reportRef = useRef(null);
  const widgetData = useRef(null);
  const reportData = useRef({});
  const distanceData = useRef({});

  const [ isLoading, setIsLoading ] = useState(true);
  const [ selected, setSelected ] = useState();
  const [ categories, setCategories ] = useState();

  const handleSelect = useCallback(async (option, filters, constants) => {
    if (!reportData.current[option.idx]) {
      if (constants.driveModeMap[option.idx].graph) {
        const query = { driveMode: option.idx };
        const response = await getStatsData(DATE_RANGES[filters.range], MODULE_TYPES.AVERAGE_RUNNING, query);
        const grouped = processReportData(response, "dst");   // Grouped Model-wise
        reportData.current[option.idx] = processData(grouped, constants);
      }
    }

    if (!distanceData.current[option.idx]) {
      const query = { driveMode: option.idx };
      const data = await getStatsData(DATE_RANGES[filters.range], [MODULE_TYPES.TOTAL_RUNNING_DST], query);
      if (data) {
        const distance = data[0] ? data[0].dst : 0;
        distanceData.current[option.idx] = (distance / 1000).toFixed(2);
      }
    }

    setSelected(option);
  }, []);

  const handleFetchData = useCallback(async (filters, constants) => {
    let switcherOptions = [];
    let categories = [];
    setIsLoading(true);

    if (!widgetData.current) {
      // Used to Fetch Switcher Options
      const widgetData = await getWidgetData(DATE_RANGES[filters.range], [MODULE_TYPES.AVERAGE_RUNNING]);
      if (!widgetData) return;

      categories = widgetData[0].data || [];
      widgetData.current = categories;

      switcherOptions = [];
      Object.values(constants.driveModeMap).forEach(async (option) => {
        switcherOptions.push({
          idx: option.idx, label: `${constants.driveModeMap[option.idx].label}`,
        });
      });

      let defaultSelected = switcherOptions[0];
      reportData.current = {};

      await handleSelect(defaultSelected, filters, constants);
      setCategories(switcherOptions);
      setIsLoading(false);
    }
  }, [ handleSelect ]);

  useEffect(() => {
    if (reportRef.current) reportRef.current.apply();
  }, [ handleFetchData ]);

  const getFilteredData = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  const getFilteredLegends = (data) => {
    const modelSet = new Set(filters.models);
    return data.filter((d) => modelSet.has(d.modelId));
  };

  return (
    <Report breadcrumb={paths} title={"Average Running Distance"} isLoading={isLoading} onApply={handleFetchData} ref={reportRef}>
      {!isLoading && (
        <>
          <div className="panel-chart-switch">
            <div />
            <Switcher
              options={categories}
              selectedOption={selected.idx}
              handleChange={(option) => handleSelect(option, filters, constants)}
              className="panel-graph-switcher"
            />
            <div />
          </div>

          {reportData.current[selected.idx] && (
            <>
              <div className="panel-chart-header">
                <ChartLegend data={getFilteredLegends(reportData.current[selected.idx].legends)} />
                <ModelPicker />
              </div>

              <div className="panel-chart-main">
                <label className="panel-chart-label">
                  KMs/day, All Vehicles
                </label>

                <Stack data={getFilteredData(reportData.current[selected.idx].graphs)} labels={labelData} parser={parser} title="Running Distance" />
              </div>

              <div className="panel-chart-info">
                <span className="widget-info-key">Total Distance: </span>
                <span className="widget-info-value">{distanceData.current[selected.idx]} km</span>
              </div>
            </>
          )}
        </>
      )}
    </Report>
  );
}

export default AverageRunningReport;
